import { Container, Paper, Title, Button, Text } from "@mantine/core";
import { useContext, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Select, TextInput } from "react-hook-form-mantine";

import { AuthContext } from "../../services/context";
import { getCurrentWorkspace } from "../../services/userConfig";
import { useCreateTicket } from "../../api/tickets";
import { confirmDuplicateName } from "./confirmDuplicateName";
import { useGetPeopleTagsBySubtype } from "../../api/tag";
import { capitalize } from "../../utils/textUtils";
import { Link } from "react-router-dom";
import { fullFormats } from "ajv-formats/dist/formats";
import { web_createTicketSchema } from "@congrs/schemas";
import resolver from "@congrs/resolver";

function CreateTicket() {
  const { user } = useContext(AuthContext);
  const currentWorkspace = getCurrentWorkspace(user);
  const currentEventId = currentWorkspace.eventId;

  const { data, isSuccess } = useGetPeopleTagsBySubtype({
    eventId: currentEventId,
    subtype: "categoryOfUser",
  });

  const categoryOfUserOptions = data?.map((tag) => ({
    value: String(tag.id),
    label: capitalize(tag.title),
  }));
  const attendeeTag = data?.find((tag) => tag.systemKey === "attendee");

  return (
    <Container size={500}>
      <Title>Create Ticket</Title>
      <Paper withBorder shadow="md" p={16} mt={16} radius="md">
        {isSuccess && (
          <Form
            currentEventId={currentEventId}
            tags={categoryOfUserOptions}
            attendeeTagId={attendeeTag && String(attendeeTag.id)}
          />
        )}
      </Paper>
    </Container>
  );
}

const Form = ({ currentEventId, tags, attendeeTagId }) => {
  const { mutate, isPending, isSuccess } = useCreateTicket();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: {
      firstName: "",
      lastName: "",
      email: "",
      companyName: "",
      categoryOfUser: attendeeTagId,
    },
    resolver: resolver(web_createTicketSchema, {
      formats: fullFormats,
      coerceTypes: true,
    }),
  });

  useEffect(() => {
    if (isSuccess) {
      reset();
    }
  }, [isSuccess, reset]);

  const createTicket = (data) =>
    confirmDuplicateName({
      eventId: currentEventId,
      firstName: data.firstName,
      lastName: data.lastName,
      onConfirm: () =>
        mutate({
          eventId: currentEventId,
          userData: {
            ...data,
            email: data.email || null,
            categoryOfUser: data.categoryOfUser
              ? [Number(data.categoryOfUser)]
              : undefined,
          },
        }),
    });

  return (
    <form onSubmit={handleSubmit((data) => createTicket(data))}>
      <TextInput
        label="First Name"
        placeholder="First Name"
        type="text"
        name="firstName"
        control={control}
        withAsterisk
        mt="md"
      />
      <TextInput
        label="Last Name"
        placeholder="Last Name"
        type="text"
        name="lastName"
        control={control}
        withAsterisk
        mt="md"
      />
      <TextInput
        label="Email"
        placeholder="Email"
        type="email"
        name="email"
        control={control}
        description="Optional. Used to send the ticket email; without one, share the ticket code or link."
        mt="md"
      />
      <TextInput
        label="Company"
        placeholder="Company"
        type="text"
        name="companyName"
        control={control}
        mt="md"
      />
      {/* If there are no categories, display message for creating one */}
      {tags?.length > 0 ? (
        <Select
          label="Category"
          placeholder="Category"
          name="categoryOfUser"
          control={control}
          mt="md"
          data={tags}
          allowDeselect={false}
        />
      ) : (
        <Text size="sm" c="dimmed" mt="sm">
          No categories available. Please create a category tag
          <Text component={Link} to="/app/tags" c="blue">
            {" "}
            here
          </Text>
          .
        </Text>
      )}

      <Button type="submit" mt="md" loading={isPending}>
        Create Ticket
      </Button>
    </form>
  );
};

export default CreateTicket;
